import isEqual from "lodash/isEqual";
import get from "lodash/get";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import { PlatformFields, Post } from "../../../../shared/types/post";

type PlatformField = NonNullable<PlatformFields[keyof PlatformFields]>;

/**
 * Sparse / inheritance override model for per-platform "Customise" overrides.
 *
 * An override stores ONLY the fields the user explicitly changed for that
 * platform, plus the `unlinked` marker. Every field NOT present in the override
 * inherits the current shared (base) post through the existing shallow-spread
 * reads (`{ ...post, ...override }`). This removes the stale-snapshot data-loss
 * class where an override, frozen at unlink time, masked later base edits or
 * published an empty value.
 *
 * `unlinked` is a reserved marker: it is never seeded, compared, or dropped.
 */

const UNLINKED_KEY = "unlinked";

type Override = Record<string, unknown>;

/**
 * Create a fresh sparse override. Replaces the old "snapshot every visible
 * field from base" builder — nothing is copied, so all fields inherit base
 * until the user edits them on the platform's own tab.
 */
export function createUnlinkedOverride(): PlatformField {
  return { unlinked: true };
}

/**
 * For a dotted field path (e.g. "gmbEvent.title"), ensure the override carries
 * the WHOLE parent object before a nested `set`. Without this, a sparse
 * override would hold a partial parent (e.g. `{ gmbEvent: { title } }`) and the
 * shallow read merge would replace base's whole `gmbEvent`, dropping sibling
 * sub-fields (startDate/endDate). Seeding a deep clone of the base parent keeps
 * those siblings. No-op for non-dotted paths (they merge key-wise correctly).
 */
export function seedOverrideParentForPath(
  override: PlatformField,
  basePost: Partial<Post>,
  fieldString: string
): void {
  if (!fieldString.includes(".")) {
    return;
  }
  const parentKey = fieldString.split(".")[0];
  const o = override as Override;
  if (o[parentKey] === undefined) {
    const baseParent = get(basePost, parentKey);
    set(o, parentKey, baseParent === undefined ? {} : cloneDeep(baseParent));
  }
}

/**
 * Convert a legacy FULL-SNAPSHOT override into a sparse one by dropping every
 * top-level key whose value deep-equals the base post's value at that key.
 * `unlinked` is always retained. Operates at top-level-key granularity only
 * (never descends into objects) so it never manufactures a partial nested
 * object.
 *
 * Behavior-preserving: a key equal to base is inert under `{ ...post, ...override }`,
 * so dropping it cannot change what the post publishes today — it only lets
 * FUTURE base edits inherit for that field.
 */
export function sparsifyOverride(
  override: PlatformField,
  basePost: Partial<Post>
): PlatformField {
  const result: Override = { unlinked: true };
  for (const key of Object.keys(override) as (keyof Override)[]) {
    if (key === UNLINKED_KEY) {
      continue;
    }
    const overrideValue = (override as Override)[key];
    if (!isEqual(overrideValue, get(basePost, key as string))) {
      result[key as string] = overrideValue;
    }
  }
  return result as PlatformField;
}

/**
 * Apply `sparsifyOverride` to every platform in a `platformFields` map against
 * the shared base post. Idempotent and inert on already-sparse or empty input,
 * so it is safe to apply at more than one hydration seam.
 */
export function sparsifyPlatformFields(
  platformFields: PlatformFields,
  basePost: Partial<Post>
): PlatformFields {
  const result: PlatformFields = {};
  for (const platform of Object.keys(
    platformFields
  ) as (keyof PlatformFields)[]) {
    const override = platformFields[platform];
    if (!override) {
      continue;
    }
    result[platform] = sparsifyOverride(override, basePost);
  }
  return result;
}
